// Admin CourseDetailPage — one course with Sessions, Enrollments and Details tabs.
// Reads via GET /api/courses/:id (+ /stats), /api/sessions?courseId and /api/enrollments/course/:id;
// the only writer is PATCH /api/courses/:id (Details tab). Sessions are created by the shared
// CreateSessionForm — no session fetch/create logic lives here beyond list state.
import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useFetch, patchJson } from "../../hooks/useApi.js";
import { useConfig } from "../../hooks/useConfig.js";
import Badge from "../../components/shared/Badge.js";
import PaymentStatusBadge from "../../components/shared/PaymentStatusBadge.js";
import CreateSessionForm from "../../components/admin/CreateSessionForm.js";
import { formatDate, formatDateTime } from "../../lib/formatDateTime.js";
import { formatKes } from "../../lib/formatMoney.js";
import type { ActiveTutor } from "../../lib/adminTutorTypes.js";
import type {
  Course, CourseStats, PaginatedResult, RosterEntry, Session, SessionWithTutor,
} from "@dm-lms/shared/types";

type Tab = "sessions" | "enrollments" | "details";
const TABS: { id: Tab; title: string }[] = [
  { id: "sessions",    title: "Sessions" },
  { id: "enrollments", title: "Enrollments" },
  { id: "details",     title: "Details" },
];

const INPUT_CLASS =
  "mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-dm-navy focus:border-dm-royal focus:outline-none focus:ring-1 focus:ring-dm-royal";
const LABEL_CLASS = "block text-sm font-medium text-dm-navy";
const HINT_CLASS = "mt-1 text-xs text-dm-slate";
const SAVED_MESSAGE_MS = 3000;
const SKELETON_ROWS = 3;

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <p className="text-xs text-dm-slate">{label}</p>
      <p className="mt-1 text-xl font-semibold text-dm-navy">{value}</p>
    </div>
  );
}

function SkeletonRows({ cols }: { cols: number }) {
  return (
    <>
      {Array.from({ length: SKELETON_ROWS }, (_, row) => (
        <tr key={row} className="animate-pulse">
          {Array.from({ length: cols }, (_, col) => (
            <td key={col} className="px-4 py-3"><div className="h-3 rounded bg-gray-200" /></td>
          ))}
        </tr>
      ))}
    </>
  );
}

// ---- Sessions tab ----
function SessionsTab({ course, courseTutorName }: { course: Course; courseTutorName: string }) {
  const { data, isLoading, error } = useFetch<SessionWithTutor[]>(`/api/sessions?courseId=${course.id}`);
  const [sessions, setSessions] = useState<SessionWithTutor[] | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (data) setSessions(data);
  }, [data]);

  function handleCreated(session: Session) {
    // The create response carries no tutor name; the course's tutor is the default
    // and an override is resolved on the next load.
    const tutorName = session.tutorId === null ? courseTutorName : null;
    setSessions((prev) => [{ ...session, tutorName }, ...(prev ?? [])]);
    setShowForm(false);
    setNotice(`Session "${session.title}" created`);
  }

  const now = Date.now();
  const rows = isLoading ? null : sessions;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        {notice ? <p className="text-sm text-green-700">{notice}</p> : <span />}
        <button
          type="button"
          onClick={() => { setShowForm((shown) => !shown); setNotice(null); }}
          className="rounded-md bg-dm-navy px-4 py-2 text-sm font-medium text-white transition hover:bg-dm-blue"
        >
          {showForm ? "Close" : "Add session"}
        </button>
      </div>

      {showForm && (
        <div className="mb-4">
          <CreateSessionForm
            courseId={course.id}
            courseTutorId={course.tutorId}
            courseTutorName={courseTutorName}
            onSuccess={handleCreated}
            onCancel={() => { setShowForm(false); }}
          />
        </div>
      )}

      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-dm-slate">
            <tr>
              <th className="px-4 py-3 font-medium">#</th>
              <th className="px-4 py-3 font-medium">Title</th>
              <th className="px-4 py-3 font-medium">Scheduled</th>
              <th className="px-4 py-3 font-medium">Duration</th>
              <th className="px-4 py-3 font-medium">Mode</th>
              <th className="px-4 py-3 font-medium">Tutor</th>
              <th className="px-4 py-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows === null && !error && <SkeletonRows cols={7} />}
            {rows?.map((session, index) => (
              <tr key={session.id}>
                <td className="px-4 py-3 text-dm-slate">{index + 1}</td>
                <td className="px-4 py-3 font-medium text-dm-navy">
                  <Link to={`/app/admin/sessions/${session.id}`} className="hover:underline">{session.title}</Link>
                </td>
                <td className="px-4 py-3">{formatDateTime(session.scheduledAt)}</td>
                <td className="px-4 py-3">{session.durationMinutes} min</td>
                <td className="px-4 py-3"><Badge tone="gray">{session.mode}</Badge></td>
                <td className="px-4 py-3">{session.tutorName ?? "—"}</td>
                <td className="px-4 py-3">
                  {/* Derived from the date, never stored */}
                  <Badge tone={new Date(session.scheduledAt).getTime() >= now ? "blue" : "gray"}>
                    {new Date(session.scheduledAt).getTime() >= now ? "Upcoming" : "Past"}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows?.length === 0 && (
          <p className="px-4 py-6 text-sm text-dm-slate">No sessions yet. Add the first one above.</p>
        )}
      </div>
    </div>
  );
}

// ---- Enrollments tab (read-only) ----
function EnrollmentsTab({ courseId }: { courseId: string }) {
  const { data, isLoading, error } = useFetch<PaginatedResult<RosterEntry>>(
    `/api/enrollments/course/${courseId}?page=1&pageSize=100`
  );
  const rows = isLoading ? null : data?.items;

  return (
    <div>
      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-dm-slate">
            <tr>
              <th className="px-4 py-3 font-medium">Student code</th>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Enrolled</th>
              <th className="px-4 py-3 font-medium">Payment</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows === null && !error && <SkeletonRows cols={4} />}
            {rows?.map((entry) => (
              <tr key={entry.id}>
                <td className="px-4 py-3 font-medium text-dm-navy">{entry.studentCode}</td>
                <td className="px-4 py-3">{entry.studentName}</td>
                <td className="px-4 py-3">{formatDate(entry.enrolledAt)}</td>
                <td className="px-4 py-3"><PaymentStatusBadge status={entry.paymentStatus} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows?.length === 0 && <p className="px-4 py-6 text-sm text-dm-slate">No students enrolled yet.</p>}
      </div>
    </div>
  );
}

// ---- Details tab ----
interface DetailsForm {
  title: string;
  description: string;
  mode: string;
  capacity: string;
  price: string;
  accountRef: string;
  isPublished: boolean;
}

function toForm(course: Course): DetailsForm {
  return {
    title:       course.title,
    description: course.description,
    mode:        course.mode,
    capacity:    String(course.capacity),
    price:       String(course.priceKes),
    accountRef:  course.accountRef ?? "",
    isPublished: course.isPublished,
  };
}

// The PATCH body: only fields that differ from the last saved course.
function changedFields(saved: DetailsForm, form: DetailsForm): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (form.title.trim() !== saved.title) body.title = form.title.trim();
  if (form.description.trim() !== saved.description) body.description = form.description.trim();
  if (form.mode !== saved.mode) body.mode = form.mode;
  if (form.capacity !== saved.capacity) body.capacity = Number(form.capacity);
  if (form.price !== saved.price) body.price = Number(form.price);
  if (form.accountRef.trim() !== saved.accountRef) body.accountRef = form.accountRef.trim() || null;
  if (form.isPublished !== saved.isPublished) body.isPublished = form.isPublished;
  return body;
}

function DetailsTab({ course, onSaved }: { course: Course; onSaved: (course: Course) => void }) {
  const modes = useConfig("course_mode");
  const [saved, setSaved] = useState(() => toForm(course));
  const [form, setForm] = useState(saved);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    if (!justSaved) return;
    const timer = setTimeout(() => { setJustSaved(false); }, SAVED_MESSAGE_MS);
    return () => { clearTimeout(timer); };
  }, [justSaved]);

  const body = useMemo(() => changedFields(saved, form), [saved, form]);
  const isDirty = Object.keys(body).length > 0;

  function update<K extends keyof DetailsForm>(key: K, value: DetailsForm[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setJustSaved(false);
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!isDirty) return;
    setError(null);
    setIsSaving(true);
    try {
      const res = await patchJson<Course>(`/api/courses/${course.id}`, body);
      if (!res.ok) {
        setError(res.error);
        return;
      }
      const next = toForm(res.data);
      setSaved(next);
      setForm(next);
      setJustSaved(true);
      onSaved(res.data);
    } catch {
      setError("Unable to save changes. Please try again.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <form onSubmit={(event) => { void handleSubmit(event); }} className="space-y-4 rounded-lg border border-gray-200 bg-white p-6">
      <div>
        <label htmlFor="course-title" className={LABEL_CLASS}>Title</label>
        <input id="course-title" type="text" required minLength={3} maxLength={200} value={form.title}
          onChange={(event) => { update("title", event.target.value); }} className={INPUT_CLASS} />
      </div>

      <div>
        <label htmlFor="course-description" className={LABEL_CLASS}>Description</label>
        <textarea id="course-description" required rows={4} minLength={10} maxLength={2000} value={form.description}
          onChange={(event) => { update("description", event.target.value); }} className={INPUT_CLASS} />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <label htmlFor="course-mode" className={LABEL_CLASS}>Mode</label>
          <select id="course-mode" required value={form.mode} disabled={modes.loading}
            onChange={(event) => { update("mode", event.target.value); }} className={INPUT_CLASS}>
            {modes.options.map((option) => (
              <option key={option.key} value={option.key}>{option.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="course-capacity" className={LABEL_CLASS}>Capacity</label>
          <input id="course-capacity" type="number" required min={1} max={200} value={form.capacity}
            onChange={(event) => { update("capacity", event.target.value); }} className={INPUT_CLASS} />
        </div>
        <div>
          <label htmlFor="course-price" className={LABEL_CLASS}>Price (KES)</label>
          <input id="course-price" type="number" required min={0} value={form.price}
            onChange={(event) => { update("price", event.target.value); }} className={INPUT_CLASS} />
          <p className={HINT_CLASS}>This is the amount students pay to enroll</p>
        </div>
      </div>

      <div>
        <label htmlFor="course-account-ref" className={LABEL_CLASS}>M-Pesa account prefix</label>
        <input id="course-account-ref" type="text" maxLength={20} placeholder="e.g. MF" value={form.accountRef}
          onChange={(event) => { update("accountRef", event.target.value); }} className={INPUT_CLASS} />
        <p className={HINT_CLASS}>Short prefix used in M-Pesa account references e.g. MF, ACM</p>
      </div>

      <div className="flex items-start gap-3">
        <input id="course-published" type="checkbox" checked={form.isPublished} className="mt-1"
          onChange={(event) => { update("isPublished", event.target.checked); }} />
        <div>
          <label htmlFor="course-published" className={LABEL_CLASS}>Published</label>
          <p className={HINT_CLASS}>Only published courses appear to students</p>
        </div>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
      {justSaved && <p className="text-sm text-green-700">Saved</p>}

      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={isSaving || !isDirty}
          className="rounded-md bg-dm-navy px-4 py-2 text-sm font-medium text-white transition hover:bg-dm-blue disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSaving ? "Saving…" : "Save changes"}
        </button>
        <button
          type="button"
          disabled={isSaving || !isDirty}
          onClick={() => { setForm(saved); setError(null); }}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm text-dm-navy disabled:opacity-60"
        >
          Discard changes
        </button>
      </div>
    </form>
  );
}

export default function CourseDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useFetch<Course>(`/api/courses/${id}`);
  const { data: stats } = useFetch<CourseStats>(`/api/courses/${id}/stats`);
  const { data: tutorsData } = useFetch<PaginatedResult<ActiveTutor>>("/api/admin/tutors/active?page=1&pageSize=100");
  const [course, setCourse] = useState<Course | null>(null);
  const [tab, setTab] = useState<Tab>("sessions");

  useEffect(() => {
    if (data) setCourse(data);
  }, [data]);

  const tutorName = tutorsData?.items.find((tutor) => tutor.userId === course?.tutorId)?.name ?? "Course tutor";

  if (isLoading) return <div className="dm-page"><p className="text-sm text-dm-slate">Loading course…</p></div>;
  if (error || !course) {
    return (
      <div className="dm-page">
        <p className="text-sm text-red-600">{error ?? "Course not found"}</p>
        <Link to="/app/admin/courses" className="text-sm text-dm-blue hover:underline">← All courses</Link>
      </div>
    );
  }

  return (
    <div className="dm-page">
      <button
        type="button"
        onClick={() => { void navigate(-1); }}
        className="mb-3 text-sm text-dm-blue hover:underline"
      >
        ← All courses
      </button>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold text-dm-navy">{course.title}</h1>
        <Badge tone="blue">{course.code}</Badge>
        <Badge tone="gray">{course.mode}</Badge>
        <Badge tone={course.isPublished ? "green" : "gold"}>{course.isPublished ? "Published" : "Draft"}</Badge>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard label="Enrolled students" value={stats?.totalEnrolled ?? "—"} />
        <StatCard label="Total sessions" value={stats?.totalSessions ?? "—"} />
        <StatCard label="Price" value={formatKes(course.priceKes)} />
      </div>

      <div role="tablist" className="mb-6 flex flex-wrap gap-1 border-b border-gray-200">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={item.id === tab}
            onClick={() => { setTab(item.id); }}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition ${
              item.id === tab
                ? "border-dm-navy text-dm-navy"
                : "border-transparent text-dm-slate hover:text-dm-navy"
            }`}
          >
            {item.title}
          </button>
        ))}
      </div>

      {tab === "sessions" && <SessionsTab course={course} courseTutorName={tutorName} />}
      {tab === "enrollments" && <EnrollmentsTab courseId={course.id} />}
      {tab === "details" && <DetailsTab course={course} onSaved={setCourse} />}
    </div>
  );
}
